import styled from "styled-components/macro";
import { typography } from "../../../utils/styledComponents/typography";

import { IPAD } from "../../../utils/constants/media";
import { colors } from "src/theme/colors";
import { radii, text } from "src/theme/ui";

/* The whole panel, not four fifths of it. At 80% the settings form gave up a
   fifth of a column that was already sharing the page with a 270px menu, so
   three fields in a row came out 138px each — narrower than the names they
   hold. */
export const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  ${typography.CustomText("1em")}
`;

export const Form = styled.div`
  /* margin-top: 4.8125rem; */
  width: 100%;

  @media (max-width: ${IPAD}px) {
    width: 100% !important;
  }
`;

/* The account type, as a badge beside the name rather than a 32px filled
   block above it. */
export const Tag = styled.div`
  display: inline-flex;
  flex: none;
  align-self: center;
  justify-content: center;
  align-items: center;
  gap: 5px;
  height: 20px;
  padding: 0 8px;
  border-radius: ${radii.pill}px;
  background-color: ${(props) => props.color};
`;

export const TagTitle = styled.span`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: white;
`;

/* Half-opacity black is a grey nobody chose: it lands between two steps of
   the ramp and changes with whatever is behind it. */
export const Small = styled.span`
  ${typography.CustomText("0.875rem")}
  font-weight: normal;
  color: ${colors.gray[500]};
`;

/**
 * The identity band at the top of the profile.
 *
 * Seventy pixels of margin under it, with the name set as an h1 in a column
 * narrow enough to break "Mehdi El Amine Fichtali" across three lines, and
 * the account's created and updated timestamps as 12px black at half opacity
 * pushed to the far right edge — the two hardest things on the panel to read
 * were the two dates, and the easiest was a name the reader already knows.
 */
export const Wrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  /* No rule and no margin under it any more: the identity band is its own
     card now, so a hairline inside it drew a second edge just above the
     card's own and left a band of blank paper between the two. */
  margin-bottom: 0;

  @media (max-width: ${IPAD}px) {
    flex-direction: column;
  }
`;

/**
 * The whole avatar is the control.
 *
 * It was a 24px chip on the circle's lower-right holding a 13px glyph — a
 * target smaller than a fingertip, and one that read as a badge rather than
 * as something to press. Football makes the picture itself the button and
 * puts the affordance ON it: the portrait dims under a scrim and a camera
 * appears, on hover and on keyboard focus alike.
 */
export const Picture = styled.div`
  position: relative;
  flex: none;
  border-radius: 50%;
  cursor: pointer;

  .ant-avatar {
    display: block;
  }
`;

export const PictureOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(15, 20, 25, 0.45);
  color: ${colors.white};
  opacity: 0;
  transition: opacity 0.16s ease;

  ${Picture}:hover &,
  ${Picture}:focus-within & {
    opacity: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/* When the account was opened and when it last changed: audit metadata, so it
   sits in the card's head rather than in the identity's own corner, on one
   line at the size the rest of the product's captions use. */
export const Timestamp = styled.div`
  display: flex;
  flex: none;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  line-height: 1.4;
  color: ${colors.gray[500]};

  /* Gone on a phone. Two dates about the ACCOUNT are the least important
     thing on a panel about the person, and beside the card's own title in a
     420px head they were colliding with it. */
  @media (max-width: 560px) {
    display: none;
  }
`;

export const Profile = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: row;
  align-items: center;

  @media (max-width: ${IPAD}px) {
    flex-direction: column;
  }
`;

export const Name = styled.h1`
  margin: 0;
  ${text.hero}
  /* 16, the reference's own size. At 18 the name was the loudest thing on a
     panel whose job is the twelve fields under it, and a full name plus a
     tier badge then ran out of the row on a narrow section. */
  font-size: 16px;
  letter-spacing: -0.015em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.3;
  text-transform: capitalize;
  color: ${colors.gray[900]};
`;

export const Action = styled.div``;

/* The profession, under the name — an editable line, so it keeps the pencil,
   but at the size of a subtitle rather than of body copy in its own block. */
export const Job = styled.div`
  .ant-typography {
    margin: 0 !important;
    font-size: 12.5px;
    color: ${colors.gray[500]};
  }
`;

export const ProfileDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  margin-left: 16px;

  @media (max-width: ${IPAD}px) {
    margin: 0;
    margin-top: 1em;
    align-items: center;
  }
`;
